// src/pages/PlatformAdminDashboard.js

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebase';
import { collection, getDocs, query, orderBy, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FaBuilding, FaPlusCircle, FaChartBar, FaWrench, FaRobot } from 'react-icons/fa';
import './PlatformAdminDashboard.css';

const PlatformAdminDashboard = () => {
    const { userData } = useAuth();
    // Normaliza métricas para evitar desajustes de nombres/estructura
    const rawStats = userData?.platformStats || {};
    const fraccRaw = rawStats.fraccionamientos || rawStats.fracc || {};
    const solicitudesRaw = rawStats.solicitudes || rawStats.registros || {};
    const metricasRaw = rawStats.metricas || rawStats.kpis || {};

    const [fraccCounts, setFraccCounts] = useState({ activos: null, inactivos: null, total: null, expirando: null });
    const fraccStatsNormalized = {
        activos: fraccRaw.activos ?? fraccRaw.active ?? 0,
        pendientes: fraccRaw.pendientes ?? fraccRaw.pending ?? 0,
        total: fraccRaw.total ?? fraccRaw.count ?? (fraccRaw.activos || 0) + (fraccRaw.pendientes || 0)
    };

    const [solicitudCounts, setSolicitudCounts] = useState({ pendientes: null, contactadas: null, rechazadas: null, total: null });
    const solicitudesStatsNormalized = {
        pendientes: solicitudesRaw.pendientes ?? solicitudesRaw.pending ?? 0,
        contactadas: solicitudesRaw.contactadas ?? solicitudesRaw.contacted ?? 0,
        rechazadas: solicitudesRaw.rechazadas ?? solicitudesRaw.rejected ?? 0,
        total: solicitudesRaw.total ?? solicitudesRaw.count ?? null,
    };

    const [metricasStats, setMetricasStats] = useState({
        usuariosReales: metricasRaw.total ?? 0,
        loginUsuarios: metricasRaw.usuarios ?? 0,
        crecimiento: metricasRaw.crecimiento ?? metricasRaw.growth ?? 'N/A'
    });
    const [loadingMetricas, setLoadingMetricas] = useState(true);

    // Cargar métricas reales desde Firestore
    useEffect(() => {
        const loadMetrics = async () => {
            try {
                // Intentar obtener métricas actuales
                const metricsDoc = await getDoc(doc(db, 'platformMetrics', 'current'));

                if (metricsDoc.exists()) {
                    const data = metricsDoc.data();
                    setMetricasStats({
                        usuariosReales: data.usuarios?.total || 0,
                        loginUsuarios: data.usuarios?.activosUnicos || data.usuarios?.activosUnicosMes || 0,
                        crecimiento: data.usuarios?.crecimiento !== undefined
                            ? `${data.usuarios.crecimiento >= 0 ? '+' : ''}${data.usuarios.crecimiento.toFixed(1)}%`
                            : 'N/A'
                    });
                    setLoadingMetricas(false);
                } else {
                    // Si no existen métricas, intentar actualizarlas
                    const updateMetrics = httpsCallable(functions, 'updatePlatformMetrics');
                    try {
                        const result = await updateMetrics();
                        if (result.data.success && result.data.metrics) {
                            const metrics = result.data.metrics;
                            setMetricasStats({
                                usuariosReales: metrics.usuarios?.total || 0,
                                loginUsuarios: metrics.usuarios?.activosUnicos || metrics.usuarios?.activosUnicosMes || 0,
                                crecimiento: metrics.usuarios?.crecimiento !== undefined
                                    ? `${metrics.usuarios.crecimiento >= 0 ? '+' : ''}${metrics.usuarios.crecimiento.toFixed(1)}%`
                                    : 'N/A'
                            });
                        }
                    } catch (updateError) {
                        console.error('Error actualizando métricas:', updateError);
                    }
                    setLoadingMetricas(false);
                }
            } catch (error) {
                console.error('Error cargando métricas:', error);
                setLoadingMetricas(false);
            }
        };

        loadMetrics();

        // Suscribirse a actualizaciones en tiempo real
        const unsubscribe = onSnapshot(doc(db, 'platformMetrics', 'current'), (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                setMetricasStats({
                    usuariosReales: data.usuarios?.total || 0,
                    loginUsuarios: data.usuarios?.activosUnicos || data.usuarios?.activosUnicosMes || 0,
                    crecimiento: data.usuarios?.crecimiento !== undefined
                        ? `${data.usuarios.crecimiento >= 0 ? '+' : ''}${data.usuarios.crecimiento.toFixed(1)}%`
                        : 'N/A'
                });
            }
        });

        return () => unsubscribe();
    }, []);

    // Fallback a conteo real desde Firestore si las métricas del contexto no están disponibles o son 0
    useEffect(() => {
        const shouldFetch = !fraccStatsNormalized.total || fraccStatsNormalized.total === 0;
        if (!shouldFetch) return;
        const fetchFraccCounts = async () => {
            try {
                const fraccQuery = query(collection(db, 'fraccionamientos'), orderBy('fechaCreacion', 'desc'));
                const snapshot = await getDocs(fraccQuery);
                const docs = snapshot.docs.map((d) => ({ id: d.id, data: d.data() }));
                let activos = 0;
                let inactivos = 0;
                let expirando = 0;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                docs.forEach(({ data }) => {
                    const estado = (data.estado || '').toLowerCase();
                    if (estado === 'activo') activos += 1;
                    else if (estado === 'inactivo') inactivos += 1;
                    const fv = data.fechaVencimiento?.toDate ? data.fechaVencimiento.toDate() : null;
                    if (fv) {
                        const diffDays = Math.ceil((fv.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        if (diffDays > 0 && diffDays <= 7) expirando += 1;
                    }
                });
                setFraccCounts({ activos, inactivos, total: docs.length, expirando });
            } catch (err) {
                console.error('No se pudo obtener conteo de fraccionamientos', err);
                setFraccCounts((prev) => ({ ...prev, activos: 0, inactivos: 0, total: 0, expirando: 0 }));
            }
        };
        fetchFraccCounts();
    }, [fraccStatsNormalized.total]);

    // Solicitudes: obtener conteos reales desde Firestore si faltan o son 0
    useEffect(() => {
        const shouldFetch = !solicitudesStatsNormalized.total || solicitudesStatsNormalized.total === 0;
        if (!shouldFetch) return;
        const fetchSolicitudCounts = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'solicitudes_registro'));
                let pendientes = 0;
                let contactadas = 0;
                let rechazadas = 0;
                snapshot.docs.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    const estadoRaw = [data.estado, data.status, data.estatus]
                        .map((v) => (typeof v === 'string' ? v.toLowerCase().trim() : ''))
                        .find((v) => v);
                    switch (estadoRaw) {
                        case 'pendiente':
                        case 'pendientes':
                            pendientes += 1;
                            break;
                        case 'contactada':
                        case 'contactadas':
                        case 'contactado':
                        case 'contactados':
                            contactadas += 1;
                            break;
                        case 'rechazada':
                        case 'rechazadas':
                        case 'rechazado':
                        case 'rechazados':
                            rechazadas += 1;
                            break;
                        default:
                            // otros estados ignorados en conteo principal
                            break;
                    }
                });
                setSolicitudCounts({ pendientes, contactadas, rechazadas, total: snapshot.size });
            } catch (err) {
                console.error('No se pudo obtener conteo de solicitudes_registro', err);
                setSolicitudCounts((prev) => ({ ...prev, pendientes: 0, contactadas: 0, rechazadas: 0, total: 0 }));
            }
        };
        fetchSolicitudCounts();
    }, [solicitudesStatsNormalized.total]);

    const fraccStats = useMemo(() => ({
        activos: (fraccCounts.activos ?? fraccStatsNormalized.activos) || 0,
        porVencer: (fraccCounts.expirando ?? 0),
        total: (fraccCounts.total ?? fraccStatsNormalized.total) || 0,
    }), [fraccCounts, fraccStatsNormalized]);

    return (
        <div className="platform-dashboard-container">
            <div className="dashboard-cards-grid">
                {/* Tarjeta para Configuración General */}
                <div className="dashboard-card">
                    <div className="card-header-row">
                        <div className="card-icon-box blue">
                            <FaWrench />
                        </div>
                        <h3>Configuración General</h3>
                    </div>
                    <p className="card-description">Ajusta parámetros globales, planes de suscripción y plantillas del sistema para personalizar la experiencia.</p>

                    <div className="card-stats-row">
                        <div className="stat-item">
                            <strong className="text-green">✔</strong>
                            <span>SISTEMA OK</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">3</strong>
                            <span>PLANES</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">12</strong>
                            <span>PLANTILLAS</span>
                        </div>
                    </div>

                    <div className="card-actions">
                        <Link to="/configuracion-plataforma" className="card-btn primary">Acceder a Configuración</Link>
                    </div>
                </div>

                {/* Tarjeta para Gestionar Fraccionamientos */}
                <div className="dashboard-card">
                    <div className="card-header-row">
                        <div className="card-icon-box blue">
                            <FaBuilding />
                        </div>
                        <h3>Gestionar Fraccionamientos</h3>
                    </div>
                    <p className="card-description">Administra y visualiza todos los fraccionamientos registrados en el sistema. Edita configuraciones o desactiva fraccionamientos según sea necesario.</p>

                    <div className="card-stats-row">
                        <div className="stat-item">
                            <strong className="text-green">{fraccStats.activos}</strong>
                            <span>ACTIVOS</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-orange">{fraccStats.porVencer}</strong>
                            <span>POR VENCER</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">{fraccStats.total}</strong>
                            <span>TOTAL</span>
                        </div>
                    </div>

                    <div className="card-actions">
                        <Link to="/fraccionamientos" className="card-btn primary">Ver Registros</Link>
                    </div>
                </div>

                {/* Tarjeta para Solicitudes de Registro */}
                <div className="dashboard-card">
                    <div className="card-header-row">
                        <div className="card-icon-box blue">
                            <FaPlusCircle />
                        </div>
                        <h3>Solicitudes de Registro</h3>
                    </div>
                    <p className="card-description">Gestiona las solicitudes pendientes de nuevos fraccionamientos o crea un nuevo registro de forma manual directamente en el sistema.</p>

                    <div className="card-stats-row">
                        <div className="stat-item">
                            <strong className="text-green">{(solicitudCounts.pendientes ?? solicitudesStatsNormalized.pendientes) || 0}</strong>
                            <span>PENDIENTES</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">{(solicitudCounts.contactadas ?? solicitudesStatsNormalized.contactadas) || 0}</strong>
                            <span>CONTACTO</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-orange">{(solicitudCounts.rechazadas ?? solicitudesStatsNormalized.rechazadas) || 0}</strong>
                            <span>RECHAZADAS</span>
                        </div>
                    </div>

                    <div className="card-actions row">
                        <Link to="/solicitudes-demo" className="card-btn primary">Gestionar Demo</Link>
                        <Link to="/soporte/solicitudes" className="card-btn outline">Soporte</Link>
                    </div>
                </div>

                {/* Tarjeta para Métricas */}
                <div className="dashboard-card">
                    <div className="card-header-row">
                        <div className="card-icon-box blue">
                            <FaChartBar />
                        </div>
                        <h3>Métricas de la Plataforma</h3>
                    </div>
                    <p className="card-description">Analiza el crecimiento, uso y facturación general de toda la plataforma con datos actualizados en tiempo real.</p>

                    <div className="card-stats-row">
                        <div className="stat-item">
                            <strong className="text-purple">{metricasStats.usuariosReales}</strong>
                            <span>USUARIOS REALES</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-orange">{metricasStats.loginUsuarios}</strong>
                            <span>LOGIN USUARIOS</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">{metricasStats.crecimiento}</strong>
                            <span>CRECIMIENTO</span>
                        </div>
                    </div>

                    <div className="card-actions">
                        <Link to="/metricas" className="card-btn primary">Ver Análisis Completo</Link>
                    </div>
                </div>

                {/* Tarjeta para Calidad y Satisfacción del Asistente IA */}
                <div className="dashboard-card">
                    <div className="card-header-row">
                        <div className="card-icon-box blue" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                            <FaRobot />
                        </div>
                        <h3>Calidad del Asistente IA</h3>
                    </div>
                    <p className="card-description">Monitorea en tiempo real las calificaciones (👍 / 👎), precisión de respuestas y comentarios de residentes y administradores.</p>

                    <div className="card-stats-row">
                        <div className="stat-item">
                            <strong className="text-green">👍 IA</strong>
                            <span>FEEDBACK VIVO</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-purple">100%</strong>
                            <span>AUDITORÍA</span>
                        </div>
                        <div className="stat-item">
                            <strong className="text-blue">CALIBRAR</strong>
                            <span>PROMPTS</span>
                        </div>
                    </div>

                    <div className="card-actions">
                        <Link to="/calidad-chatbot" className="card-btn primary" style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>Ver Evaluaciones IA</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlatformAdminDashboard;